// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { APP_LOCALE } from "./constants.js";

export function isSameMonth(isoA, isoB) {
  const a = new Date(isoA), b = new Date(isoB);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}


// The device's own IANA timezone, read once at household creation (LB-316). Wrapped
// because `Intl.DateTimeFormat().resolvedOptions().timeZone` is undefined on a handful of
// old WebViews and can throw behind aggressive privacy shims — either way a household
// simply has no timezone and the worker falls back to Europe/London.
//
// Read at CREATION only, never backfilled by migrateState: the device that happens to save
// next may be a child's tablet on a school trip, and guessing wrong would move a
// household's email hour without anyone asking for it (principle 3).
export function browserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz.trim() ? tz.trim() : null;
  } catch {
    return null;
  }
}

// The ONE stamp every transaction row shows, on any screen size (Alan, 13 Sep). It replaces
// fmtTime ("Today, 3:45 PM", kid lists) and fmtTimestamp ("14:32 12/09/26", parent lists) at
// every ActivityRow call site — two formats from two helpers, neither of which a phone ever
// showed, because ActivityRow rendered the stamp with `hidden sm:inline` and so hid it on
// every screen narrower than 640px. That class is what "we have lost transaction dates"
// actually was; it was never lost data.
//
// Sixteen characters is what made someone reach for a breakpoint rather than a layout, so
// the format is the fix as much as the class is:
//   today              14:32       a bare time in a newest-first list reads as today
//   earlier this year  12 Sep      short month, so it can't be read the American way round
//   last year or older 12 Sep 25   the year only appears when it is doing work
// Nine characters at worst, which is what lets it stay inline on a 360px row.
//
// Yesterday is deliberately not special-cased: "Yesterday, 14:32" is the longest string of
// the lot and buys nothing over "12 Sep". fmtTimestamp is kept for the audit log and
// anywhere else an exact stamp is the point.
export function fmtActivityStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const day = d.toLocaleDateString(APP_LOCALE, { day: "numeric", month: "short" });
  return d.getFullYear() === now.getFullYear() ? day : `${day} ${String(d.getFullYear() % 100).padStart(2, "0")}`;
}

export function fmtTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return sameDay ? `Today, ${time}` : `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

// Precise hh:mm dd/mm/yy format, used wherever an exact timestamp matters (requests, ad-hoc activity).

export function fmtTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const dd = pad(d.getDate());
  const mo = pad(d.getMonth() + 1);
  const yy = pad(d.getFullYear() % 100);
  return `${hh}:${mm} ${dd}/${mo}/${yy}`;
}

/* ------------------------------ daily habits & streaks --------------------------
 * Pure date/streak helpers shared by the kid-facing streak card and the parent's
 * Rewards tab. Days are plain "YYYY-MM-DD" strings in the browser's local calendar
 * (habits are a real-world daily thing, not a UTC one), so a household's "today"
 * always matches the day printed on their kitchen calendar.
 * ---------------------------------------------------------------------------- */


export function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateStrAddDays(dateStr, delta) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// Monday-start week containing dateStr.

export function weekStartOf(dateStr) {
  const day = new Date(dateStr + "T00:00:00").getDay(); // 0=Sun..6=Sat
  return dateStrAddDays(dateStr, day === 0 ? -6 : 1 - day);
}

export function weekDatesFrom(weekStart) {
  return Array.from({ length: 7 }, (_, i) => dateStrAddDays(weekStart, i));
}

// Whole days between two yyyy-mm-dd strings (to - from). Rounded, not floored, so a DST
// hour either side of a boundary can't turn "14 days" into 13.
export function daysBetween(fromStr, toStr) {
  const from = new Date(fromStr + "T00:00:00").getTime();
  const to = new Date(toStr + "T00:00:00").getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / 86400000);
}
